import React, { useEffect, useState } from 'react';
import ActionTooltip from 'src/components/ActionTooltip';
import TooltipCmp from 'src/components/TooltipCmp';

interface IProps {
    message: string;
    refName: React.RefObject<HTMLElement | null>;
    tooltipType?: 'action' | 'default';
    children?: React.ReactNode;
    parentClassName?: string;
    onClick?: () => void;
    truncateTextParentClassName?: string;
    // When true, suppresses this component's own tooltip-on-truncate even if the text
    // overflows — for callers that already show the full value in an outer tooltip
    // (e.g. an ActionTooltip wrapping this component) and only need the truncation
    // measurement/CSS, not a second nested tooltip.
    noTooltip?: boolean;
}

const TruncateTextCmp = ({
    message,
    refName,
    children,
    truncateTextParentClassName = '',
    tooltipType = 'default',
    parentClassName = 'max-w-[200px] lg:max-w-[400px] w-max',
    onClick,
    noTooltip = false,
}: IProps) => {
    const [isMessageTruncated, setIsMessageTruncated] = useState(false);

    useEffect(() => {
        // Pending rAF handle, so a deferred measurement can be cancelled on unmount.
        let frame: number | null = null;

        const measure = () => {
            const el = refName.current;
            if (!el) return;
            const next = el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;
            // Returning prev unchanged makes a no-op measurement a true no-op — no re-render,
            // so a stable element never re-enters the observe -> setState -> resize cycle.
            setIsMessageTruncated((prev) => (prev === next ? prev : next));
        };

        // Measurement is deliberately DEFERRED out of the ResizeObserver callback.
        //
        // Flipping isMessageTruncated wraps the observed node in a tooltip element, which resizes
        // that same node, which re-triggers the observer. Measuring synchronously inside the
        // callback makes that whole cycle happen within one layout pass, and the browser reports
        // "ResizeObserver loop completed with undelivered notifications" (escalated to a red
        // overlay by the react-scripts dev server). rAF lets the current pass finish first, so each
        // measurement settles in its own frame and the cycle terminates.
        const scheduleMeasure = () => {
            if (frame !== null) return; // coalesce a burst of notifications into one measurement
            frame = requestAnimationFrame(() => {
                frame = null;
                measure();
            });
        };

        // First pass stays synchronous so freshly-mounted truncated text gets its tooltip
        // immediately, with no one-frame flicker.
        measure();
        window.addEventListener('resize', scheduleMeasure);

        const resizeObserver = new ResizeObserver(scheduleMeasure);
        if (refName.current) resizeObserver.observe(refName.current);

        return () => {
            if (frame !== null) cancelAnimationFrame(frame);
            window.removeEventListener('resize', scheduleMeasure);
            resizeObserver.disconnect();
        };
    }, [message, refName]);

    return (
        <div className={`overflow-hidden ${truncateTextParentClassName}`}>
            {isMessageTruncated && !noTooltip ? (
                tooltipType === 'action' ? (
                    <ActionTooltip type="light" parentClassName={parentClassName} isOpenChildTopPosOnTop={true} openOnHover={true} tooltipPosition="center" message={message} onClick={onClick}>
                        {children}
                    </ActionTooltip>
                ) : (
                    <TooltipCmp message={message} parentClassName={parentClassName} childrenClassName="!block">
                        {children}
                    </TooltipCmp>
                )
            ) : (
                children
            )}
        </div>
    );
};

export default TruncateTextCmp;
